import { type ActivityLevel, type DietaryPreference } from "./MealsPlan";
import { type Gender } from "./shared";
import { type ClientExperience, type PlaceToWorkout } from "./SurveyTemplate";

// TRE-1604 — structured client profile built from onboarding survey answers (survey 2.0)
// merged with trainee columns. Read through the `get_client_profile` RPC (single query,
// RLS client-read) and normalized with `normalizeClientProfile` from @fitness-app/utils.
// Not to be confused with ClientGoal.ts (client_goal table = water/steps/weight targets).

export enum ClientProfileGoal {
  WeightLoss = "weightLoss",
  Recomposition = "recomposition",
  Energy = "energy",
  Health = "health",
}

export enum TimePerWeek {
  TwoToThree = "twoToThree",
  FourToFive = "fourToFive",
  DailyShort = "dailyShort",
}

export enum PreferredTrainingTime {
  Morning = "morning",
  Daytime = "daytime",
  Evening = "evening",
  Flexible = "flexible",
}

export enum TrainingLimitation {
  Knees = "knees",
  Spine = "spine",
  Hips = "hips",
  ShouldersOrWrists = "shouldersOrWrists",
  Hypertension = "hypertension",
  PregnancyOrPostpartum = "pregnancyOrPostpartum",
  None = "none",
}

export enum WorkType {
  Sedentary = "sedentary",
  Standing = "standing",
  Physical = "physical",
  Mixed = "mixed",
}

export enum SleepDuration {
  LessThanSix = "lessThanSix",
  SixToSeven = "sixToSeven",
  SevenToEight = "sevenToEight",
  MoreThanEight = "moreThanEight",
}

export enum MotivationEventType {
  Wedding = "wedding",
  Vacation = "vacation",
  Other = "other",
  None = "none",
}

/**
 * Preferred training weekdays (survey question key "schedule" — the legacy
 * "Wybierz dni treningowe" question reused by survey 2.0, so historical answers
 * keep merging in). Values match the lowercase `WeekDay` union used across
 * training-program scheduling, so the AI program generator can map them straight
 * onto `defaultSchedule`.
 */
export enum TrainingDay {
  Monday = "monday",
  Tuesday = "tuesday",
  Wednesday = "wednesday",
  Thursday = "thursday",
  Friday = "friday",
  Saturday = "saturday",
  Sunday = "sunday",
}

/**
 * Protected survey question keys consumed by the profile. Existing keys
 * (height, weight, birthYear, gender, experience, placeToWorkout, activityLevel,
 * dietaryPreference) are reused so historical surveys keep mapping; the rest are
 * introduced by the low-ticket survey 2.0 template.
 */
export const CLIENT_PROFILE_SURVEY_KEYS = [
  "height",
  "weight",
  "birthYear",
  "gender",
  "goal",
  "targetWeight",
  "experience",
  "placeToWorkout",
  "timePerWeek",
  "schedule",
  "preferredTrainingTime",
  "limitations",
  "limitationsNote",
  "previousAttempts",
  "motivationEvent",
  "motivationEventDate",
  "motivation",
  "workType",
  "sleepDuration",
  "activityLevel",
  "dietaryPreference",
  "contactPhone",
] as const;

export type ClientProfileSurveyKey = (typeof CLIENT_PROFILE_SURVEY_KEYS)[number];

/** Raw result of the `get_client_profile(p_trainee_id)` RPC. */
export interface ClientProfileRpcResult {
  traineeId: string;
  trainee: {
    firstName: string | null;
    email: string | null;
    gender: string | null;
    birthday: string | null;
    height: number | null;
    weight: number | null;
    dietaryPreference: number | null;
    type: string | null;
    status: string | null;
  };
  /** Latest answer per protected question key, merged across initial surveys. */
  answers: Record<string, unknown>;
  surveyIds: string[];
  answeredAt: string | null;
}

export interface ClientProfile {
  traineeId: string;
  firstName: string | null;
  email: string | null;
  gender: Gender | null;
  birthYear: number | null;
  height: number | null;
  weight: number | null;
  targetWeight: number | null;
  goal: ClientProfileGoal | null;
  experience: ClientExperience | null;
  placeToWorkout: PlaceToWorkout | null;
  timePerWeek: TimePerWeek | null;
  /** Preferred training weekdays (survey key "schedule"); empty when unanswered. */
  trainingDays: TrainingDay[];
  preferredTrainingTime: PreferredTrainingTime | null;
  limitations: TrainingLimitation[];
  limitationsNote: string | null;
  previousAttempts: string | null;
  motivation: string | null;
  motivationEvent: MotivationEventType | null;
  motivationEventDate: string | null;
  workType: WorkType | null;
  sleepDuration: SleepDuration | null;
  activityLevel: ActivityLevel | null;
  dietaryPreference: DietaryPreference | null;
  contactPhone: string | null;
  surveyIds: string[];
  answeredAt: string | null;
}
